import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function Testimonials() {
  const testimonials = [
    {
      id: 1,
      name: "Karthick K",
      role: "Web Developer",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=150&h=150",
      text: "Simply the best educational platform! After joining, I'm now placed in MNC. Very helpful, and coding explanation was top notch!"
    },
    {
      id: 2,
      name: "Priya S",
      role: "Student",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150&h=150",
      text: "The learning is very good and easy to understand. Visual explanations are very helpful. Highly recommended!"
    },
    {
      id: 3,
      name: "Dinesh M",
      role: "Designer",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=150&h=150",
      text: "I learned design here and it changed my career. Best mentor and support system. I recommend it to all!"
    },
    {
      id: 4,
      name: "Meenakshi",
      role: "Student",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=150&h=150",
      text: "Wonderful learning experience. The course structure is very detailed and explanation is very easy to follow."
    }
  ];

  const [activeDot, setActiveDot] = useState(0);
  const scrollRef = useRef(null);

  // Check scroll position to update dots
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, clientWidth, scrollWidth } = scrollRef.current;
    
    // Calculate current slide
    // Max scroll left
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) return;
    
    const percentage = scrollLeft / maxScroll;
    // Map percentage to 3 dots (0, 1, 2)
    const dotIndex = Math.min(2, Math.max(0, Math.round(percentage * 2)));
    setActiveDot(dotIndex);
  };

  const scrollToDot = (dotIndex) => {
    if (!scrollRef.current) return;
    const { clientWidth, scrollWidth } = scrollRef.current;
    const maxScroll = scrollWidth - clientWidth;
    
    // Map dot index (0, 1, 2) to scroll left position
    const scrollTarget = (dotIndex / 2) * maxScroll;
    
    scrollRef.current.scrollTo({
      left: scrollTarget,
      behavior: 'smooth'
    });
    setActiveDot(dotIndex);
  };

  return (
    <section id="testimonials" className="py-16 bg-gradient-to-tr from-white via-amber-50/10 to-orange-50/20 relative overflow-hidden dot-pattern">
      <div className="absolute top-0 right-0 w-96 h-96 -mr-20 -mt-20 rounded-full bg-amber-100 opacity-40 blur-3xl animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 -ml-20 -mb-20 rounded-full bg-orange-100 opacity-40 blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      
      <div className="absolute -top-10 left-[5%] text-[250px] leading-none text-orange-50/40 font-serif font-black select-none pointer-events-none z-0 rotate-12">"</div>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
            What <span className="text-orange-500">Our Students</span> Say
          </h2>
          <p className="text-gray-500 text-sm md:text-base font-medium">
            Real people, Real stories.
          </p>
        </div>

        {/* Testimonials Cards Slider/Grid */}
        <div 
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex lg:grid lg:grid-cols-4 gap-6 overflow-x-auto lg:overflow-x-visible scrollbar-none snap-x snap-mandatory pb-6 px-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {testimonials.map((t, index) => {
            const avatarColors = [
              "bg-blue-100 text-blue-600 border-blue-100",
              "bg-green-100 text-green-600 border-green-100",
              "bg-purple-100 text-purple-600 border-purple-100",
              "bg-rose-100 text-rose-600 border-rose-100",
              "bg-teal-100 text-teal-600 border-teal-100"
            ];
            const colorClass = avatarColors[index % avatarColors.length];
            return (
            <div 
              key={t.id}
              className="min-w-[280px] sm:min-w-[340px] lg:min-w-0 flex-1 snap-start snap-always"
            >
              <div className="bg-white rounded-3xl p-8 h-full border border-orange-100 transition-all duration-300 hover:border-orange-300 hover:-translate-y-2 flex flex-col justify-between group relative overflow-hidden">
                <div className="absolute -bottom-4 -right-4 text-9xl text-orange-50/30 font-serif leading-none select-none pointer-events-none transition-transform duration-500 group-hover:scale-110 group-hover:text-orange-100/40">"</div>
                <div className="relative z-10">
                  {/* User Profile Header */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-full border-2 shrink-0 flex items-center justify-center font-bold text-xl uppercase ${colorClass}`}>
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-base">{t.name}</h4>
                      <p className="text-xs text-gray-500">{t.role}</p>
                    </div>
                  </div>

                  {/* 5 Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating)].map((_, i) => (
                      <svg 
                        key={i} 
                        className="w-4 h-4 fill-current text-orange-500" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-gray-600 text-sm leading-relaxed text-justify">
                    "{t.text}"
                  </p>
                </div>
              </div>
            </div>
          )})}
        </div>

        {/* Carousel Indicator Dots (Hidden on large desktop grid) */}
        <div className="flex lg:hidden justify-center items-center gap-2 mt-8">
          {[0, 1, 2].map((dotIndex) => (
            <button
              key={dotIndex}
              onClick={() => scrollToDot(dotIndex)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                activeDot === dotIndex 
                  ? 'w-8 bg-gradient-to-r from-orange-500 to-amber-500' 
                  : 'w-2.5 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${dotIndex + 1}`}
            />
          ))}
        </div>

        <div className="mt-16 flex justify-center z-10 relative">
          <Link to="/login" className="shimmer-btn inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold py-4 px-10 rounded-full transition-transform duration-300 hover:scale-105">
            Start Your Journey Now
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
